import { useRef } from 'react';
import { motion } from 'framer-motion';

export default function ToonCard({ toon, onHover, onLeave, onSelect }) {
  const ref = useRef(null);
  const rotate = { x: 0, y: 0 };

  const onMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    rotate.x = -py * 10;
    rotate.y = px * 12;
    ref.current.style.setProperty('--rx', `${rotate.x}deg`);
    ref.current.style.setProperty('--ry', `${rotate.y}deg`);
  };

  const onLeaveCard = () => {
    ref.current.style.setProperty('--rx', '0deg');
    ref.current.style.setProperty('--ry', '0deg');
    onLeave();
  };

  return (
    <motion.button
      ref={ref}
      data-testid={`toon-card-${toon.index}`}
      data-cursor="toon"
      onMouseMove={onMove}
      onMouseEnter={() => onHover(toon)}
      onMouseLeave={onLeaveCard}
      onClick={(e) => onSelect(toon, e)}
      className="group relative min-w-[270px] snap-center rounded-3xl border p-5 pb-6 text-left transition-colors duration-300 sm:min-w-[300px] lg:min-w-[330px]"
      style={{
        transform: 'perspective(900px) rotateX(var(--rx, 0deg)) rotateY(var(--ry, 0deg))',
        transformStyle: 'preserve-3d',
        borderColor: `${toon.accent}40`,
        background: `linear-gradient(180deg, ${toon.accentSoft} 0%, rgba(8,21,43,0.85) 45%)`,
        boxShadow: `0 20px 50px rgba(5,11,24,0.55), inset 0 0 0 rgba(0,200,255,0)`
      }}
      whileHover={{ y: -14 }}
      whileTap={{ scale: 0.98 }}
      transition={{ type: 'spring', stiffness: 260, damping: 20 }}
      initial={{ opacity: 0, y: 44 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      aria-label={`Open ${toon.role} profile`}
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-3xl opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        style={{ boxShadow: `0 0 44px ${toon.accent}55, inset 0 0 26px ${toon.accent}22`, borderColor: toon.accent }}
      />
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] tracking-[0.3em]" style={{ color: toon.accent }}>
          TOON // {toon.index}
        </span>
        <span className="rounded-full border border-dashed border-gold/50 px-2.5 py-1 font-mono text-[8px] uppercase tracking-[0.25em] text-gold">
          {toon.status}
        </span>
      </div>

      <div className="relative mx-auto my-2 flex h-[340px] items-center justify-center sm:h-[380px]">
        <div
          aria-hidden="true"
          className="absolute h-2/3 w-2/3 rounded-full opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100"
          style={{ background: `radial-gradient(circle, ${toon.accent}44 0%, transparent 70%)` }}
        />
        <motion.img
          src={toon.image}
          alt={`${toon.role} — ARC TOONS character`}
          loading="lazy"
          className="relative max-h-full w-auto object-contain transition-all duration-300 group-hover:scale-[1.06]"
          style={{ filter: 'drop-shadow(0 16px 30px rgba(5,11,24,0.6))' }}
        />
      </div>

      <div className="relative" style={{ transform: 'translateZ(30px)' }}>
        <h3
          className="font-display text-xl font-extrabold uppercase tracking-tight"
          style={{ color: toon.accent }}
          data-testid={`toon-card-role-${toon.index}`}
        >
          {toon.role}
        </h3>
        <p className="mt-1 text-xs text-snow/60" data-testid={`toon-card-tagline-${toon.index}`}>
          {toon.tagline}
        </p>
        <span className="mt-3 inline-flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.3em] text-snow/45 transition-colors duration-300 group-hover:text-electric">
          VIEW PROFILE
          <span aria-hidden="true" className="transition-transform duration-300 group-hover:translate-x-1">→</span>
        </span>
      </div>
    </motion.button>
  );
}
